import { useCallback, useEffect, useState } from "react";

import { countStoredChunks, isDebugLogStoreAvailable } from "lib/webrtc/debugLogStore";
import { useWebRTCDebug } from "providers/WebRTCDebugProvider";

const DeveloperWebRTCPanel: React.FC = () => {
    const { supported, enabled, setEnabled, clearLogs, storageError } = useWebRTCDebug();
    const [storedChunks, setStoredChunks] = useState<number | null>(null);

    const refreshStoredChunks = useCallback(() => {
        if (!isDebugLogStoreAvailable()) return;
        countStoredChunks().then(setStoredChunks, () => setStoredChunks(null));
    }, []);

    useEffect(() => {
        refreshStoredChunks();
    }, [refreshStoredChunks, enabled]);

    return (
        <section className="mt-6 max-w-5xl">
            <h3 className="text-h5 font-semibold text-primary2">WebRTC debug</h3>

            <div className="mt-4 flex items-start gap-4">
                <div className="min-w-0 flex-1 text-h5 text-black40">
                    <p>
                        Logs the H.264 SPS and PPS of each video stream and every SDP exchanged, with per-stream
                        receive statistics, in a floating popup on the control screen.
                    </p>
                    <p className="mt-2">
                        Logging starts as soon as this is switched on and continues while the popup is collapsed.
                        Entries are saved in this browser in 500 KiB chunks
                        {storedChunks === null ? "." : ` (${storedChunks} stored).`}
                    </p>
                    {!supported ? (
                        <p className="mt-2 text-warning">Not available with the Janus video transport.</p>
                    ) : null}
                    {storageError ? <p className="mt-2 text-warning">Log storage: {storageError}</p> : null}
                    <button
                        type="button"
                        onClick={() => clearLogs().then(refreshStoredChunks)}
                        className="mt-3 rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80"
                    >
                        Clear stored logs
                    </button>
                </div>

                <div className="w-44 shrink-0 rounded-r10 border border-black80 bg-black100 px-3 py-3">
                    <p className="text-h5 font-semibold text-white">WebRTC logging</p>
                    <div className="mt-4 flex items-center justify-between gap-2">
                        <span className="text-h5 tabular-nums text-black40">{enabled ? "On" : "Off"}</span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={enabled}
                            aria-label="Enable WebRTC debug logging"
                            disabled={!supported}
                            onClick={() => setEnabled(!enabled)}
                            className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border p-0.5 transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary2/90 focus-visible:ring-offset-2 focus-visible:ring-offset-black90 disabled:cursor-not-allowed disabled:opacity-50 ${
                                enabled ? "justify-end border-primary2 bg-primary2" : "justify-start border-black80 bg-black70"
                            }`}
                        >
                            <span className="pointer-events-none h-5 w-5 rounded-full bg-white shadow" aria-hidden />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DeveloperWebRTCPanel;
